// The embeddable sign-up form: the one script a customer pastes into their own
// website. Everything else in this directory is reached from here.
//
// THE RULES FOR EVERYTHING IN app/javascript/embed, all four pinned by
// test/integration/embed_bundle_test.rb because every one of them fails
// invisibly on somebody else's website:
//
//   1. Nothing is imported from outside this directory. Importing turbo-rails
//      would start Turbo on a stranger's page and take over their links and
//      their forms; importing ./controllers would call Application.start() and
//      write window.Stimulus. Even app/javascript/lib is barred -- it is ours,
//      but it is weight a customer's visitors would download for nothing.
//
//   2. No innerHTML, ever. Every node is createElement + textContent. This
//      widget renders server-supplied strings on every site that embeds it, so
//      one innerHTML here is stored XSS with as many blast radii as we have
//      customers.
//
//   3. No user-facing copy. Every word comes from the config
//      (Signups::FormDefinition), including the A2P disclosures, because the
//      page Twilio's reviewers approved is generated from that same object. A
//      string typed here is compliance copy no review will ever see.
//
//   4. The bundle is built --format=iife (see package.json). A classic
//      <script src> cannot execute ESM: `export {}` is a SyntaxError and the
//      widget dies before its first line. It builds either way; it only fails
//      in a browser, on a website we do not own.
//
//   5. No destructuring, anywhere. esbuild refuses to downlevel it for our
//      --target floor of safari12 -- it believes older Safari has a bug it
//      cannot work around -- so the choice is between writing `const {a} = b`
//      and supporting an old iPhone. The build fails loudly if this is broken,
//      which is the one rule here that cannot ship by accident.

import { sweep } from "./mount"

// Valid only during synchronous execution, and null under eval -- which some
// page builders use. A hint for the short-form snippet and for working out
// where MessageKit is, never the mechanism for finding a mount point.
const CURRENT = document.currentScript

// Versioned, so a future /embed/v2/ bundle on the same page coexists with this
// one rather than short-circuiting it.
const KEY = "__mkSignupV1"

if (window[KEY]) {
  // A second copy of this exact bundle: the customer pasted the snippet twice,
  // or their CMS injected the footer code into the header as well. Sweep for
  // containers this instance has not claimed -- two pasted forms should be two
  // forms -- but do not install a second observer.
  window[KEY].sweep()
} else {
  window[KEY] = { sweep }
  boot()
}

function boot() {
  sweep(CURRENT)

  // Squarespace swaps page content over AJAX. It may re-run our script, may
  // fail to run it at all (a <script> inserted via innerHTML never executes),
  // and never fires a document load. One debounced sweep on any DOM change
  // covers all of that, plus code blocks that render late and back-forward
  // cache restores.
  //
  // Re-entry is safe by construction rather than by a guard flag: a sweep is
  // one querySelectorAll against a claimed-element stamp, so the mutations our
  // own rendering causes find nothing to do.
  let pending = null
  const later = () => {
    clearTimeout(pending)
    pending = setTimeout(() => sweep(), 50)
  }

  new MutationObserver(later).observe(document.documentElement, {
    childList: true,
    subtree: true
  })
  window.addEventListener("popstate", later)
  window.addEventListener("pageshow", later)
}
